import { ol_ImageLayer, ol_VectorLayer, ol_Coordinate, ol_Extent, ol_Feature, ol_source, ol_style, ol_geom, ol_layer } from "@omnia/exports/openlayers";
import { GcpContext } from "./gcp-context";
import { GcpMap } from "./gcp-map";
import { GcpPoint } from "./gcp-point";
import { IGeorefReprojectedImage } from "./types";

export class TargetMap extends GcpMap {

    private basemapLayer: ol_layer.Layer;
    private boundaryLayer: ol_VectorLayer;

    private _boundaryFeature: ol_Feature;
    private _fiedFeature: ol_Feature;
    private _customBoundary: boolean;

    private resultsLayer: ol_ImageLayer;

    private drawingLayer: ol_layer.Layer;

    public reprojectedImage: IGeorefReprojectedImage;

    constructor(gcpContext: GcpContext, private bingKey: string) {
        super(gcpContext, true);
    }

    public setupEditor(fieldBoundary: ol_geom.Geometry, boundary: ol_geom.Geometry, extent: ol_Extent) {

        let fieldFeature = new ol_Feature(fieldBoundary);
        fieldFeature.setStyle(this.getFieldBoundaryStyles());
        this._fiedFeature = fieldFeature;

        let source = this.boundaryLayer.getSource();
        source.clear();
        source.addFeature(fieldFeature);

        this.setBoundary(boundary);

        this.viewExtent(extent);

    }

    get fieldBoundary(): ol_geom.Geometry {
        return this._fiedFeature.getGeometry();
    }

    get boundary(): ol_geom.Geometry {
        return this._boundaryFeature.getGeometry();
    }

    get showFieldBoundary(): boolean {
        return this.boundaryLayer.getVisible();
    }

    set showFieldBoundary(value: boolean) {
        this.boundaryLayer.setVisible(value);
    }

    hasCustomBoundary(): boolean {
        return this._customBoundary;
    }

    setBoundary(geom: ol_geom.Geometry) {

        let boundary = new ol_Feature(geom || this.fieldBoundary);
        boundary.setStyle(this.getBoundaryStyles());

        let source = this.boundaryLayer.getSource();

        if (this._boundaryFeature) {
            source.removeFeature(this._boundaryFeature);
        }

        source.addFeature(boundary);
        this._boundaryFeature = boundary;
        this._customBoundary = !!geom;

    }

    public addNewPoint(coord: ol_Coordinate): GcpPoint {
        return this.context.addPoint(coord, false);
    }

    public getPointFeature(point: GcpPoint): ol_Feature {
        return point.map;
    }

    public setupMap(targetElement: HTMLElement) {

        super.setupMap(targetElement);

        this.basemapLayer = this.createBasemapLayer(this.bingKey);
        this.olmap.addLayer(this.basemapLayer);

        this.resultsLayer = new ol_layer.Image({
            source: null,
            visible: false,
        });

        this.olmap.addLayer(this.resultsLayer);

        this.boundaryLayer = this.createBoundaryLayer();
        this.olmap.addLayer(this.boundaryLayer);

        this.olmap.addLayer(this.pointsLayer);

    }

    private createBoundaryLayer() {

        let source = new ol_source.Vector({
            useSpatialIndex: false
        });

        let layer = new ol_layer.Vector({
            source: source
        });

        return layer;
    }

    private getBoundaryStyles(): ol_style.Style[] {

        let strokeWidth = 2;
        let red = [220, 20, 60];

        let outerStroke = new ol_style.Style({
            stroke: new ol_style.Stroke({
                color: [255, 255, 255, 1],
                width: strokeWidth + 2
            })
        });

        let innerStroke = new ol_style.Style({
            stroke: new ol_style.Stroke({
                color: red,
                width: strokeWidth
            })
        });

        return [
            outerStroke,
            innerStroke
        ];


    }

    private getFieldBoundaryStyles(): ol_style.Style[] {

        return [
            new ol_style.Style({
                stroke: new ol_style.Stroke({
                    color: [0, 0, 0],
                    lineDash: [6, 6],
                    width: 2
                })
            })
        ];

    }

    private createBasemapLayer(bingKey: string): ol_layer.Layer {

        let basemap = new ol_layer.Tile({
            source: new ol_source.BingMaps({
                key: bingKey,
                imagerySet: 'AerialWithLabels',
                maxZoom: 19,
                placeholderTiles: true
            }),
            opacity: 1
        });

        this.basemapLayer = basemap;

        return basemap;

    }

    get reprojectedImageOpacity(): number {
        return this.resultsLayer.getOpacity() * 100;
    }

    set reprojectedImageOpacity(value: number) {
        this.resultsLayer.setOpacity(value / 100);
    }

    updateReprojectedImage() {

        this.reprojectedImage = this.context.reprojectedImage;
        let img = this.reprojectedImage;

        if (!img) {
            this.resultsLayer.setVisible(false);
            return;
        }

        let source = new ol_source.ImageStatic({
            imageExtent: img.extent,
            url: img.imageUrl,
            projection: "EPSG:3857"
        });

        this.resultsLayer.setSource(source);
        this.resultsLayer.setVisible(true);

        if (!this.resultsLayer.getOpacity()) {
            this.resultsLayer.setOpacity(0.5);
        }

    }

    insertDrawingLayer(layer: ol_layer.Layer) {

        this.removeDrawingLayer();

        let layers = this.olmap.getLayers();
        layers.insertAt(layers.getLength() - 2, layer);

        this.drawingLayer = layer;

    }

    removeDrawingLayer() {

        if (this.drawingLayer) {
            this.olmap.removeLayer(this.drawingLayer);
            this.drawingLayer = null;
        }

    }
}
